import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Autoria, EncabezadoTabla, FilaTabla, Marca, TablaAncha } from '../../ui/base';
import { instalacion } from '@tanteo/compartido/instalacion';

/** Los dos documentos legales, enlazados desde la Portada y desde el pie de cada uno. */
export function EnlacesLegales() {
  return (
    <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1" aria-label="Documentos legales">
      <Link to="/legal/terminos-servicio" className="underline" data-e2e="enlace-terminos">
        Términos de servicio
      </Link>
      <Link to="/legal/politica-datos" className="underline" data-e2e="enlace-politica-datos">
        Política de datos
      </Link>
    </nav>
  );
}

/**
 * Armazón público de un documento legal: la marca y el pie de la Portada, el bloque de versión y el
 * aviso de borrador. Se ve igual con sesión o sin ella.
 */
export function PaginaLegal({ titulo, children }: { titulo: string; children: ReactNode }) {
  useEffect(() => {
    document.title = `${titulo} · ${instalacion.nombre}`;
    window.scrollTo(0, 0);
  }, [titulo]);

  return (
    <main className="flex min-h-full flex-col" data-e2e="pagina-legal">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/" aria-label={`Inicio de ${instalacion.nombre}`}>
            <Marca className="text-marca-grande" />
          </Link>
          <Link to="/" className="text-primary-deep font-bold underline">
            Volver al inicio
          </Link>
        </div>
      </header>

      <article className="mx-auto w-full max-w-5xl px-4 py-10">
        <div className="firma-degradado mb-6 h-1.5 w-24 rounded-full" aria-hidden="true" />
        <h1 className="max-w-3xl text-portada font-extrabold">{titulo}</h1>
        <p className="rounded-card bg-surface/60 border-border mt-6 max-w-3xl border p-4 text-contenido">
          <strong>Versión 0.1 (borrador)</strong> · 7 de octubre de 2026 · En revisión jurídica. Los campos marcados{' '}
          <Pendiente>[TBD: …]</Pendiente> están por definir.
        </p>
        <div className="max-w-3xl text-contenido">{children}</div>
      </article>

      <footer className="border-border text-muted mt-auto border-t">
        <div className="mx-auto w-full max-w-5xl space-y-2 px-4 py-4 text-center text-etiqueta">
          <EnlacesLegales />
          <p>By CARDAVIL</p>
        </div>
        <Autoria className="pb-4" />
      </footer>
    </main>
  );
}

/** Un capítulo o cláusula del documento: su título y sus párrafos. */
export function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="mb-3 text-titulo font-extrabold">{titulo}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** Lista con viñetas, o numerada con `numerada`. */
export function Lista({ children, numerada = false }: { children: ReactNode; numerada?: boolean }) {
  return numerada ? (
    <ol className="list-decimal space-y-1 pl-6">{children}</ol>
  ) : (
    <ul className="list-disc space-y-1 pl-6">{children}</ul>
  );
}

/** Un dato que el borrador deja por definir, resaltado para que no pase por texto definitivo. */
export function Pendiente({ children }: { children: ReactNode }) {
  return <span className="bg-warning/20 px-1">{children}</span>;
}

/** Tabla del documento: una fila de títulos y filas de texto. */
export function TablaLegal({ titulos, filas }: { titulos: string[]; filas: ReactNode[][] }) {
  return (
    <TablaAncha className="text-contenido">
      <EncabezadoTabla>
        {titulos.map((t) => (
          <th key={t} className="py-2 pr-3">
            {t}
          </th>
        ))}
      </EncabezadoTabla>
      <tbody>
        {filas.map((fila, i) => (
          <FilaTabla key={i}>
            {fila.map((celda, j) => (
              <td key={j} className="py-2 pr-3 align-top">
                {celda}
              </td>
            ))}
          </FilaTabla>
        ))}
      </tbody>
    </TablaAncha>
  );
}
